import { storage } from 'firebaseConfig';
import { ref as fireRef, uploadBytesResumable, getDownloadURL } from 'firebase/storage';
import moment from 'moment';
import * as Sentry from '@sentry/react';

/**
 * Uploads a file to Firebase Storage.
 *
 * @param {File} file - The file to upload.
 * @param {(progress: number) => void} [onProgress] - Optional callback to track upload progress.
 * @param {string} location - The path in Firebase Storage where the file will be stored.
 * @returns {Promise<string>} - A promise that resolves to the file's download URL.
 * @throws {Error} - If the upload or URL generation fails.
 */
export const uploadToFirebaseStorage = async (
    file: File,
    onProgress?: (progress: number) => void,
    location: string = 'admin/uploads'
): Promise<string> => {
    if (!file) {
        const error = new Error("No file provided for upload.");
        Sentry.captureException(`103 Error during Firebase uploadToFirebaseStorage ${error}.`);
        throw error;
    }

    const uniqueId = `${moment().format('YYYY-MM-DD_HH:mm:ss')}-${file.name}`;
    const fileRef = fireRef(storage, `${location}/${uniqueId}`);

    return new Promise((resolve, reject) => {
        const uploadTask = uploadBytesResumable(fileRef, file);

        uploadTask.on(
            'state_changed',
            (snapshot) => {
                if (onProgress) {
                    const progress = Math.round(
                        (snapshot.bytesTransferred / snapshot.totalBytes) * 100
                    );
                    onProgress(progress);
                }
            },
            (error) => {
                console.error("Upload error:", error);
                Sentry.captureException(`104 Error during Firebase uploadToFirebaseStorage upload ${error}.`);
                reject(new Error("Failed to upload file to Firebase Storage."));
            },
            async () => {
                try {
                    const downloadURL = await getDownloadURL(uploadTask.snapshot.ref);
                    resolve(downloadURL);
                } catch (error) {
                    console.error("Error getting download URL:", error);
                    Sentry.captureException(`105 Error during Firebase uploadToFirebaseStorage getDownloadURL ${error}.`);
                    reject(new Error("Failed to retrieve download URL."));
                }
            }
        );
    });
};